import React, { useState, useEffect } from "react";
import InstructionsPanel from "./InstructionsPanel";
import CheckedIndicator from "./CheckedIndicator";
import Dropdown from "./Dropdown";
import { useMicrophoneCheck } from "../hooks/useMicrophoneCheck";
import { detectBraveBrowser } from "../utils/deviceDetection";
import BraveBrowserWarningModal from "./modals/BraveBrowserWarningModal";
import MicrophoneErrorModal from "./modals/MicrophoneErrorModal";
import redMic from "../assets/red-mic-border.svg";
import infoIcon from "../assets/info.svg";

interface MicCheckProps {
  onMicQualityChange: (isComplete: boolean) => void;
  instructions: string[];
  sentence: string;
  footer?: React.ReactNode;
}

const MicCheck: React.FC<MicCheckProps> = ({ onMicQualityChange, instructions, sentence, footer }) => {
  const [showBraveWarning, setShowBraveWarning] = useState(false);
  const [braveWarningDismissed, setBraveWarningDismissed] = useState(false);
  const [isBraveBrowser, setIsBraveBrowser] = useState(false);

  const {
    isListening,
    isInitializing,
    isComplete,
    showAudioError,
    showMicErrorModal,
    setShowMicErrorModal,
    startListening,
    retryListening,
    renderSentence,
    microphones,
    selectedMic,
    handleMicChange,
  } = useMicrophoneCheck(sentence, onMicQualityChange);

  useEffect(() => {
    const isBrave = detectBraveBrowser();
    setIsBraveBrowser(isBrave);
  }, []);

  const checkBraveAndProceed = (action: () => void) => {
    const isBrave = detectBraveBrowser();
    if (isBrave && !braveWarningDismissed) {
      setShowBraveWarning(true);
      sessionStorage.setItem("pendingMicAction", "startTest");
    } else if (!isBrave) {
      action();
    }
  };

  const handleBraveWarningClose = () => {
    setShowBraveWarning(false);
    setBraveWarningDismissed(true);
    sessionStorage.removeItem("pendingMicAction");
  };

  const handleProceedWithBrave = () => {
    setShowBraveWarning(false);
    setBraveWarningDismissed(true);
    sessionStorage.removeItem("pendingMicAction");
  };

  const handleStartListening = () => {
    checkBraveAndProceed(startListening);
  };

  const handleMicChangeWithBraveCheck = (micId: string) => {
    const isBrave = detectBraveBrowser();
    if (isBrave && !braveWarningDismissed) {
      setShowBraveWarning(true);
      sessionStorage.setItem("pendingMicChange", micId);
    } else {
      handleMicChange(micId);
    }
  };

  useEffect(() => {
    const pendingMicChange = sessionStorage.getItem("pendingMicChange");
    if (pendingMicChange && braveWarningDismissed) {
      handleMicChange(pendingMicChange);
      sessionStorage.removeItem("pendingMicChange");
    }
  }, [braveWarningDismissed, handleMicChange]);

  const isStartTestDisabled = isComplete || isInitializing || (isBraveBrowser && braveWarningDismissed);

  const renderSentenceWithColors = () => {
    const wordElements = renderSentence();
    return wordElements.map((wordData, index) => (
      <span key={index} className={wordData.className}>
        {wordData.word}
        {!wordData.isLast ? " " : ""}
      </span>
    ));
  };

  return (
    <div className="flex flex-row gap-4 w-full">
      <div className="bg-[#FAFAFA] border-2 border-[#E6E6E6] flex flex-col gap-5 w-full p-8 rounded-xl">
        <div className="flex flex-1 gap-[50px]">
          <div className="flex flex-col flex-1 gap-4">
            <div className="flex flex-row gap-2 items-center justify-between">
              <Dropdown
                options={microphones.map((mic) => ({
                  label: mic.label || `Mic ${mic.deviceId}`,
                  value: mic.deviceId,
                }))}
                value={selectedMic || ""}
                placeholder="Select Microphone"
                onChange={handleMicChangeWithBraveCheck}
                width={50}
                dropdownWidth={50}
                className="max-w-[240px]"
              />
              {isComplete && <CheckedIndicator className="px-2" />}
            </div>

            <div className="flex flex-col h-[191px] rounded-[6px] items-center justify-between py-6 px-[14px] bg-white border border-[#E6E6E6]">
              {isComplete ? (
                <div className="w-full h-full flex items-center justify-center text-grey-8 font-ubuntu text-[18px] font-normal leading-5">
                  Mic is working great!
                </div>
              ) : showAudioError ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <div className="bg-[#FFE5E7] rounded-lg p-4 max-w-[350px]">
                    <div className="flex items-start gap-2">
                      <div className="min-w-6 min-h-6 flex items-center justify-center">
                        <img src={infoIcon} alt="info" />
                      </div>
                      <div className="text-[14px] text-[#4C4C4C] leading-[21px] font-medium">
                        We couldn't detect that sentence clearly. Try again or switch mic.
                      </div>
                    </div>
                  </div>
                  <button
                    className="px-4 h-9 rounded-[6px] font-ubuntu text-[14px] font-medium leading-5 bg-button text-white"
                    onClick={retryListening}
                    type="button"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="bg-white w-full min-h-[119px] flex flex-col gap-1 items-center justify-center">
                  <div className="text-[#666] font-ubuntu text-[14px] font-normal leading-5">
                    Say the sentence below to test your mic
                  </div>
                  <div className="font-ubuntu text-[18px] font-normal leading-5 text-center">" {renderSentenceWithColors()} "</div>
                </div>
              )}
              {!isComplete && !showAudioError && (
                <div className="flex flex-row">
                  {isListening ? (
                    <div className="text-grey-4 font-dm-sans text-[14px] font-normal leading-[22px] flex items-center gap-2">
                      <div className="w-5 h-5">
                        <img src={redMic} alt="red-mic" />
                      </div>
                      Listening...
                    </div>
                  ) : isInitializing ? (
                    <div className="text-grey-4 font-dm-sans text-[14px] font-normal leading-[22px] flex items-center gap-2">
                      <div className="w-5 h-5">
                        <img src={redMic} alt="red-mic" />
                      </div>
                      Initializing...
                    </div>
                  ) : (
                    <div className="flex items-center w-full justify-center">
                      <button
                        className={`px-3 h-6 rounded-[6px] font-ubuntu text-[14px] font-normal leading-5 ${
                          isStartTestDisabled ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-button text-white"
                        }`}
                        onClick={handleStartListening}
                        disabled={isStartTestDisabled}
                        type="button"
                      >
                        Start Test
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col flex-1">
            <InstructionsPanel instructions={instructions} />
          </div>
        </div>

        {footer && <div className="mt-4 w-full">{footer}</div>}
      </div>

      <BraveBrowserWarningModal isOpen={showBraveWarning} onClose={handleBraveWarningClose} />
      <MicrophoneErrorModal
        isOpen={showMicErrorModal}
        onClose={() => setShowMicErrorModal(false)}
        onRetry={() => {
          setShowMicErrorModal(false);
          handleProceedWithBrave();
        }}
      />
    </div>
  );
};

export default MicCheck;
